import { useEffect } from "react";

/** The breakpoint App.css lays the dashboard detail pages out for a phone at. */
const PHONE_MEDIA_QUERY = "(max-width: 768px)";

/** A box the layout never means to scroll on that axis — only code, or the keyboard, can. */
const isClipped = (el, axis) =>
  /hidden|clip/.test(window.getComputedStyle(el)[axis === "x" ? "overflowX" : "overflowY"]);

/**
 * On a phone a dashboard report never scrolls as a page; only its rows do,
 * with the legend pinned at the foot of the card. The iPhone keyboard (any
 * search box or filter search on the report) pans the page and the clipped
 * boxes around the card to keep the field in view, and can leave them there
 * once it closes, with the header pushed off the top and the legend off the
 * bottom. This puts them back whenever the keyboard goes.
 *
 * Mounted once in the app layout so every report gets it. It only touches the
 * window when the document fits the screen, and only boxes whose overflow is
 * hidden or clip, so the rows and any page that does scroll keep their place.
 */
export default function useDashboardKeyboardReset() {
  useEffect(() => {
    const mq = window.matchMedia(PHONE_MEDIA_QUERY);

    const resetShell = () => {
      if (!mq.matches) return;
      const page = document.querySelector(".dashboard-detail-page");
      if (!page || !page.querySelector(".in-law-footer")) return;
      // The keyboard is still up: leave the field where it panned it to.
      if (document.activeElement?.matches?.("input, textarea, [contenteditable]")) return;

      const root = document.scrollingElement || document.documentElement;
      const fits =
        root.scrollHeight <= window.innerHeight + 1 &&
        root.scrollWidth <= window.innerWidth + 1;
      if (fits && (window.scrollY !== 0 || window.scrollX !== 0)) {
        window.scrollTo(0, 0);
      }

      const boxes = [];
      for (let el = page; el && el !== document.body; el = el.parentElement) {
        boxes.push(el);
      }
      boxes.push(...page.querySelectorAll("*"));
      // Sideways too: a box a few pixels wider than the card shifts the card
      // and its legend off to the left.
      for (const el of boxes) {
        if (el.scrollTop !== 0 && isClipped(el, "y")) el.scrollTop = 0;
        if (el.scrollLeft !== 0 && isClipped(el, "x")) el.scrollLeft = 0;
      }
    };

    // After the keyboard has finished closing, not as the field loses focus.
    let timer;
    const onKeyboardChange = () => {
      clearTimeout(timer);
      timer = setTimeout(resetShell, 300);
    };

    document.addEventListener("focusout", onKeyboardChange);
    window.visualViewport?.addEventListener("resize", onKeyboardChange);
    window.addEventListener("orientationchange", onKeyboardChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("focusout", onKeyboardChange);
      window.visualViewport?.removeEventListener("resize", onKeyboardChange);
      window.removeEventListener("orientationchange", onKeyboardChange);
    };
  }, []);
}
